import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FeatureSelectService, MapTableService } from '@ebw-monorepo/map';
import { SplitViewService } from '@ebw-monorepo/utils';
import { SplitViewConf } from 'libs/ng-components/utils/src/lib/models/split-view-conf';

import { ActivatedRoute } from '@angular/router';
import { IMapTable } from '../../../interfaces/mapTable.interface';

import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import { AttivitaType } from '../checkin-analysis/checkin-analysis.component';
import { RaccordiCavoHomeComponent } from './raccordi-cavo-home/raccordi-cavo-home.component';
import { EbwModalService } from '@ebw-monorepo/modal';
import { RaccordoService } from '../../../services/raccordo.service';
import { catchError, debounceTime, distinctUntilChanged, filter, of, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'ebw-monorepo-fiber-joints',
  templateUrl: './fiber-joints.component.html',
  styleUrls: ['./fiber-joints.component.scss']
})
export class FiberJointsComponent implements OnInit, OnDestroy {
  public filtersForm: FormGroup;
  public typeEnum = AttivitaType;
  public buildingNameSuggestions: string[] = [];
  private _contextId = 'CableJointTable';
  private _destroy$ = new Subject<void>();

  // ------------------------------------------------------------------------------------------------

  constructor(
    private _featureSelectService: FeatureSelectService,
    private _activatedRoute: ActivatedRoute,
    private _splitViewService: SplitViewService,
    private _mapTableService: MapTableService,
    private _apiUrlService: ApiUrlService,
    private _ebwModalService: EbwModalService,
    private _raccordoService: RaccordoService
  ) {}

  // ------------------------------------------------------------------------------------------------

  async ngOnInit() {
    this.filtersForm = new FormGroup({
      building_name: new FormControl(''),
      building_alias: new FormControl(''),
      gat_sede: new FormControl('')
    });

    this.filtersForm
      .get('building_name')!
      .valueChanges.pipe(
        takeUntil(this._destroy$),
        debounceTime(600),
        tap((v) => {
          if (typeof v !== 'string' || v.length < 2) this.buildingNameSuggestions = [];
        }),
        filter((v) => typeof v === 'string' && v.length >= 2),
        distinctUntilChanged(),
        switchMap((term: string) =>
          this._raccordoService.autocompleteEdifici(term).pipe(
            catchError(() => {
              this._ebwModalService.openErrorModal({
                title: 'Errore',
                content: 'Errore nel recupero dei suggerimenti edificio'
              });
              return of([]);
            })
          )
        )
      )
      .subscribe((suggestions: string[]) => {
        this.buildingNameSuggestions = suggestions;
      });

    this._activatedRoute.queryParams.subscribe(async (params) => {
      let urn = params.urn || null;

      //se mi arriva un urn sto aprendo il pannello dal feature tree, eseguo una selectByUrn prendo il nome e eseguo una ricerca
      if (urn) {
        const { name, alias } = await this._getBuilding(urn);
        if (name || alias) {
          this.filtersForm.patchValue({ building_name: name, building_alias: alias }, { emitEvent: false });
          this.search();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  search() {
    let config: IMapTable = {
      url: this._apiUrlService.createUrl(API_URLS.FIBER_JOINTS.JOINTS.LIST),
      title: 'map.sidebar.raccordo-cavo.table.title',
      filters: this._mappedFilter()
    };

    const splitViewConf: SplitViewConf = {
      title: config.title,
      icon: 'maps_home_work'
    };

    this._splitViewService.show(RaccordiCavoHomeComponent, this._contextId, null, splitViewConf);

    this._mapTableService.setMapTableConfig(config);
  }

  public onBuildingSelected(name: string): void {
    this.filtersForm.patchValue({ building_name: name }, { emitEvent: false });
    this.buildingNameSuggestions = [];
    this.search();
  }

  public clearFilter() {
    this.filtersForm.reset();
  }

  private _mappedFilter() {
    let value = this.filtersForm.getRawValue();

    let filter = {
      nome_edificio: value.building_name || '',
      alias: value.building_alias || '',
      gat_sede: value.gat_sede || ''
    };

    return filter;
  }

  private async _getBuilding(urn) {
    if (!urn) {
      return null;
    }

    try {
      const response = await this._featureSelectService.selectByUrns([urn]);

      const feature = response.result.features[0];
      return feature.properties;
    } catch (error) {
      this._ebwModalService.openErrorModal({
        title: 'Errore',
        content: 'Errore recupero building'
      });
      return null;
    }
  }
}
